import { useTranslation } from 'react-i18next';
import {
  Award,
  BookOpen,
  Calendar,
  Check,
  ChevronDown,
  ExternalLink,
  GraduationCap,
  Languages,
  Loader2,
  PlayCircle,
  Quote,
  Star,
  Target,
  Users,
} from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { type Course } from '@/lib/championsHubApi';
import { useCourseDetail } from '@/hooks/use-courses';

type Slot = 'primary' | 'alternate';

interface CourseDetailModalProps {
  course: Course | null;
  /** Which slot (if any) is currently holding this course. */
  currentSlot: Slot | null;
  /** True if the primary slot is occupied by a DIFFERENT course. */
  primaryFilled: boolean;
  /** True if the alternate slot is occupied by a DIFFERENT course. */
  alternateFilled: boolean;
  onClose: () => void;
  onAssign: (courseId: string, slot: Slot) => void;
}

function formatDateTime(value: string | null, lang: 'en' | 'ar'): string | null {
  if (!value) return null;
  try {
    const d = new Date(value);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(d);
  } catch {
    return null;
  }
}

export function CourseDetailModal({
  course,
  currentSlot,
  primaryFilled,
  alternateFilled,
  onClose,
  onAssign,
}: CourseDetailModalProps) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const lang = isRTL ? 'ar' : 'en';

  const open = course !== null;
  const { data: detail, isLoading: detailLoading } = useCourseDetail(
    course?.slug ?? null,
    lang
  );

  // Prefer detail-endpoint values, fall back to list-endpoint values when missing.
  const title = detail?.title ?? course?.title[lang] ?? course?.title.en ?? '';
  const subtitle = detail?.subtitle ?? null;
  const partner =
    detail?.partnerName ?? course?.partnerName[lang] ?? course?.partnerName.en ?? '';
  const partnerLogo = detail?.partnerLogo ?? course?.partnerLogo;
  const partnerWebsite = detail?.partnerWebsite ?? course?.partnerWebsite;
  const description =
    detail?.longDescription ||
    detail?.description ||
    course?.description[lang] ||
    course?.description.en ||
    '';
  const category =
    detail?.categoryName ?? course?.categoryName[lang] ?? course?.categoryName.en ?? '';
  const level = detail?.level ?? course?.level;
  const duration =
    detail?.duration ??
    course?.durationBilingual?.[lang] ??
    course?.duration ??
    '';
  const deliveryLanguages =
    detail?.deliveryLanguages ?? course?.deliveryLanguages ?? [];
  const deliveryMode = detail?.deliveryMode;
  const studentCount = detail?.studentCount ?? course?.studentCount ?? 0;
  const rating = detail?.rating ?? course?.rating ?? 0;
  const reviewsCount = detail?.reviewsCount ?? 0;
  const publishedAt = detail?.publishedAt ?? course?.publishedAt ?? null;
  const enrollmentState = detail?.enrollmentState ?? course?.enrollmentState;

  const outcomes = (detail?.outcomes ?? []).filter(Boolean);
  const includes = (detail?.includes ?? []).filter(Boolean);
  const modules = (detail?.modules ?? []).filter((m) => m && (m.lessons?.length ?? 0) > 0);
  const faqs = (detail?.faqs ?? []).filter((f) => f.question && f.answer);
  const instructors = (detail?.instructors ?? []).filter((i) => i.name);
  const targetAudience =
    detail?.targetAudience ?? course?.targetAudienceBilingual?.[lang] ?? null;
  const learningExperience = detail?.learningExperience;
  const instructorQuote = detail?.instructorQuote;
  const accreditationName = detail?.accreditationName;
  const accreditationLogo = detail?.accreditationLogo;
  const accreditationDescription = detail?.accreditationDescription;
  const accreditationPoints = (detail?.accreditationPoints ?? []).filter(Boolean);
  const developerName = detail?.developerName;
  const developerLogo = detail?.developerLogo;
  const developerDescription = detail?.developerDescription;
  const developerPoints = (detail?.developerPoints ?? []).filter(Boolean);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-2xl p-0 overflow-hidden bg-card border-2 border-border/60 max-h-[88vh] flex flex-col"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        {!course ? null : (
          <>
            {/* Compact header — partner + coming-soon chip (no cover image) */}
            <div className="px-5 sm:px-6 pt-5 pb-3 flex items-center justify-between gap-3 border-b border-border/40 flex-shrink-0">
              <div className="inline-flex items-center gap-2 min-w-0">
                {partnerLogo ? (
                  <img
                    src={partnerLogo}
                    alt={partner}
                    className="max-h-5 w-auto object-contain flex-shrink-0"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                ) : (
                  <GraduationCap className="h-4 w-4 text-accent flex-shrink-0" />
                )}
                <span className="font-mono-mark text-[10px] uppercase tracking-widest text-foreground/75 truncate">
                  {partner}
                </span>
              </div>
              {enrollmentState === 'coming_soon' && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest border border-accent/50 text-accent flex-shrink-0">
                  {t('initiative.coaches.form.preferredCoursesComingSoon')}
                </span>
              )}
            </div>

            {/* Body — scrollable, tighter padding */}
            <div className="px-5 sm:px-6 pt-5 pb-2 overflow-y-auto flex-1">
              <DialogTitle
                className={cn(
                  'font-display font-black uppercase leading-[1.05] text-xl sm:text-2xl text-foreground text-balance',
                  isRTL && 'font-display-ar normal-case leading-[1.2]'
                )}
              >
                {title}
              </DialogTitle>

              {subtitle && (
                <DialogDescription className="mt-2 font-editorial-italic text-sm text-foreground/75 leading-relaxed">
                  {subtitle}
                </DialogDescription>
              )}
              {category && (
                <div className="mt-2 font-mono-mark text-[10px] uppercase tracking-widest text-muted-foreground">
                  {category}
                </div>
              )}

              {/* Meta strip */}
              <div className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-2 text-xs">
                {level && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-accent/10 text-accent border border-accent/30 font-bold uppercase tracking-wider text-[10px]">
                    <BookOpen className="h-3 w-3" />
                    {level}
                  </span>
                )}
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-surface-2 text-foreground/85 border border-border/60">
                  <Calendar className="h-3 w-3" />
                  {duration}
                </span>
                {deliveryLanguages.length > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-surface-2 text-foreground/85 border border-border/60">
                    <Languages className="h-3 w-3" />
                    {deliveryLanguages.map((l) => l.toUpperCase()).join(' · ')}
                  </span>
                )}
                {deliveryMode && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-surface-2 text-foreground/85 border border-border/60 capitalize">
                    {deliveryMode}
                  </span>
                )}
                {studentCount > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-surface-2 text-foreground/85 border border-border/60">
                    <Users className="h-3 w-3" />
                    {new Intl.NumberFormat('en-US').format(
                      studentCount
                    )}
                  </span>
                )}
                {rating > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-surface-2 text-foreground/85 border border-border/60">
                    <Star className="h-3 w-3 text-accent" fill="currentColor" />
                    {rating.toFixed(1)}
                    {reviewsCount > 0 && ` (${reviewsCount})`}
                  </span>
                )}
              </div>

              {/* Loading shimmer for the rich body */}
              {detailLoading && !detail && (
                <div className="mt-6 flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  {t('initiative.coaches.form.preferredCourseDetailLoading')}
                </div>
              )}

              {/* Description */}
              {description && (
                <div className="mt-6">
                  <div className="font-mono-mark text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                    ※ {isRTL ? 'الوصف' : 'About this course'}
                  </div>
                  <p className="text-sm leading-relaxed text-foreground/85 whitespace-pre-wrap">
                    {description}
                  </p>
                </div>
              )}

              {/* Outcomes */}
              {outcomes.length > 0 && (
                <div className="mt-6 pt-6 border-t border-border/40">
                  <div className="font-mono-mark text-[10px] uppercase tracking-widest text-accent mb-3 inline-flex items-center gap-1.5">
                    <Target className="h-3 w-3" />
                    {isRTL ? 'ما ستتعلّمه' : "What you'll learn"}
                  </div>
                  <ul className="grid sm:grid-cols-2 gap-2.5">
                    {outcomes.map((o, i) => (
                      <li key={i} className="flex gap-2 text-sm text-foreground/85 leading-snug">
                        <Check className="h-4 w-4 mt-0.5 flex-shrink-0 text-accent" strokeWidth={2.5} />
                        <span>{o}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Includes */}
              {includes.length > 0 && (
                <div className="mt-6 pt-6 border-t border-border/40">
                  <div className="font-mono-mark text-[10px] uppercase tracking-widest text-accent mb-3">
                    ※ {isRTL ? 'يتضمّن' : 'Course includes'}
                  </div>
                  <ul className="grid sm:grid-cols-2 gap-2 text-sm">
                    {includes.map((it, i) => (
                      <li key={i} className="text-foreground/85">
                        · {it}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Modules + lessons */}
              {modules.length > 0 && (
                <div className="mt-6 pt-6 border-t border-border/40">
                  <div className="font-mono-mark text-[10px] uppercase tracking-widest text-accent mb-3">
                    ※ {isRTL ? 'محتوى الكورس' : 'Course outline'}
                  </div>
                  <Accordion type="multiple" className="space-y-2">
                    {modules.map((m, idx) => (
                      <AccordionItem
                        key={m.id}
                        value={m.id}
                        className="rounded-lg border border-border/60 bg-surface-1/40 px-4"
                      >
                        <AccordionTrigger className="text-sm font-bold hover:no-underline">
                          <div className="flex items-center gap-2 text-start">
                            <span className="font-mono-mark text-[10px] text-accent uppercase tracking-widest w-8 flex-shrink-0">
                              {String(idx + 1).padStart(2, '0')}
                            </span>
                            <span>{m.title}</span>
                            {m.duration && (
                              <span className="text-[10px] font-mono-mark uppercase tracking-widest text-muted-foreground ms-2">
                                · {m.duration}
                              </span>
                            )}
                          </div>
                          <ChevronDown className="ms-auto h-4 w-4 shrink-0 transition-transform duration-200" />
                        </AccordionTrigger>
                        <AccordionContent>
                          <ul className="space-y-2 ps-10">
                            {m.lessons.map((l) => (
                              <li
                                key={l.id}
                                className="flex items-center gap-2 text-xs text-foreground/80"
                              >
                                <PlayCircle className="h-3 w-3 text-accent/70 flex-shrink-0" />
                                <span className="flex-1">{l.title}</span>
                                {l.duration && (
                                  <span className="text-[10px] text-muted-foreground font-mono-mark">
                                    {l.duration}
                                  </span>
                                )}
                                {l.isPreview && (
                                  <span className="text-[9px] font-bold uppercase tracking-widest text-accent border border-accent/40 rounded px-1.5 py-0.5">
                                    {isRTL ? 'معاينة' : 'Preview'}
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              )}

              {/* Target audience + learning experience */}
              {(targetAudience || learningExperience) && (
                <div className="mt-6 pt-6 border-t border-border/40 grid md:grid-cols-2 gap-5 text-sm">
                  {targetAudience && (
                    <div>
                      <div className="font-mono-mark text-[10px] uppercase tracking-widest text-accent mb-2">
                        {isRTL ? 'لمن هذا الكورس' : 'Who is this for'}
                      </div>
                      <p className="text-foreground/80 leading-relaxed">{targetAudience}</p>
                    </div>
                  )}
                  {learningExperience && (
                    <div>
                      <div className="font-mono-mark text-[10px] uppercase tracking-widest text-accent mb-2">
                        {isRTL ? 'تجربة التعلّم' : 'Learning experience'}
                      </div>
                      <p className="text-foreground/80 leading-relaxed">{learningExperience}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Instructor quote */}
              {instructorQuote && (
                <div className="mt-6 p-5 rounded-xl border-2 border-accent/30 bg-accent/5 relative">
                  <Quote className="absolute -top-2 start-4 h-5 w-5 text-accent bg-card px-0.5" />
                  <p className="font-editorial-italic text-base text-foreground/85 leading-relaxed pt-2">
                    "{instructorQuote}"
                  </p>
                </div>
              )}

              {/* Instructors */}
              {instructors.length > 0 && (
                <div className="mt-6 pt-6 border-t border-border/40">
                  <div className="font-mono-mark text-[10px] uppercase tracking-widest text-accent mb-3">
                    ※ {isRTL ? 'المدرّبون' : 'Instructors'}
                  </div>
                  <ul className="space-y-3">
                    {instructors.map((ins) => (
                      <li
                        key={ins.id}
                        className="flex items-start gap-3 p-3 rounded-lg bg-surface-1/40 border border-border/40"
                      >
                        {ins.avatar ? (
                          <img
                            src={ins.avatar}
                            alt={ins.name}
                            className="h-12 w-12 rounded-full object-cover flex-shrink-0 border border-border/60"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="h-12 w-12 rounded-full bg-accent/10 border border-accent/30 flex items-center justify-center flex-shrink-0">
                            <Users className="h-5 w-5 text-accent" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="font-bold text-sm text-foreground">{ins.name}</div>
                          {ins.title && (
                            <div className="text-[11px] font-mono-mark uppercase tracking-wider text-accent mt-0.5">
                              {ins.title}
                            </div>
                          )}
                          {ins.bio && (
                            <p className="mt-1.5 text-xs text-foreground/75 leading-relaxed">
                              {ins.bio}
                            </p>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Accreditation + developer */}
              {(accreditationName || developerName) && (
                <div className="mt-6 pt-6 border-t border-border/40 grid sm:grid-cols-2 gap-5">
                  {accreditationName && (
                    <div className="p-4 rounded-xl bg-surface-1/40 border border-border/60">
                      <div className="flex items-center gap-2 mb-2">
                        <Award className="h-4 w-4 text-accent" />
                        <div className="font-mono-mark text-[10px] uppercase tracking-widest text-accent">
                          {isRTL ? 'اعتماد' : 'Accreditation'}
                        </div>
                      </div>
                      {accreditationLogo && (
                        <img
                          src={accreditationLogo}
                          alt={accreditationName}
                          className="h-8 w-auto object-contain mb-2"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      )}
                      <div className="font-bold text-sm text-foreground">{accreditationName}</div>
                      {accreditationDescription && (
                        <p className="mt-1.5 text-[11px] text-foreground/70 leading-relaxed">
                          {accreditationDescription}
                        </p>
                      )}
                      {accreditationPoints.length > 0 && (
                        <ul className="mt-2 space-y-1 text-[11px] text-foreground/75">
                          {accreditationPoints.map((p, i) => (
                            <li key={i}>· {p}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                  {developerName && (
                    <div className="p-4 rounded-xl bg-surface-1/40 border border-border/60">
                      <div className="font-mono-mark text-[10px] uppercase tracking-widest text-accent mb-2">
                        {isRTL ? 'المطوّر' : 'Developed by'}
                      </div>
                      {developerLogo && (
                        <img
                          src={developerLogo}
                          alt={developerName}
                          className="h-8 w-auto object-contain mb-2"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      )}
                      <div className="font-bold text-sm text-foreground">{developerName}</div>
                      {developerDescription && (
                        <p className="mt-1.5 text-[11px] text-foreground/70 leading-relaxed">
                          {developerDescription}
                        </p>
                      )}
                      {developerPoints.length > 0 && (
                        <ul className="mt-2 space-y-1 text-[11px] text-foreground/75">
                          {developerPoints.map((p, i) => (
                            <li key={i}>· {p}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* FAQs */}
              {faqs.length > 0 && (
                <div className="mt-6 pt-6 border-t border-border/40">
                  <div className="font-mono-mark text-[10px] uppercase tracking-widest text-accent mb-3">
                    ※ {isRTL ? 'الأسئلة الشائعة' : 'FAQ'}
                  </div>
                  <Accordion type="single" collapsible className="space-y-2">
                    {faqs.map((f) => (
                      <AccordionItem
                        key={f.id}
                        value={f.id}
                        className="rounded-lg border border-border/60 bg-surface-1/40 px-4"
                      >
                        <AccordionTrigger className="text-sm font-semibold hover:no-underline text-start">
                          {f.question}
                        </AccordionTrigger>
                        <AccordionContent className="text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap">
                          {f.answer}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              )}

              {/* Footer details grid */}
              <div className="mt-7 pt-6 border-t border-border/40 grid sm:grid-cols-2 gap-5 text-sm">
                <div>
                  <div className="font-mono-mark text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                    {isRTL ? 'ضمن المبادرة' : 'Under the initiative'}
                  </div>
                  <div className="font-display font-black text-2xl text-accent leading-none">
                    {isRTL ? 'مجاناً تماماً' : 'Fully sponsored'}
                  </div>
                  <div className="text-[10px] mt-1.5 text-muted-foreground leading-relaxed">
                    {isRTL
                      ? 'بدون أي تكلفة على المدرّبين المقبولين.'
                      : 'No cost to accepted coaches.'}
                  </div>
                </div>
                <div className="space-y-2">
                  {publishedAt && (
                    <div>
                      <div className="font-mono-mark text-[10px] uppercase tracking-widest text-muted-foreground">
                        {isRTL ? 'تاريخ النشر' : 'Published'}
                      </div>
                      <div className="text-foreground/85">
                        {formatDateTime(publishedAt, lang)}
                      </div>
                    </div>
                  )}
                  {partnerWebsite && (
                    <a
                      href={partnerWebsite}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-accent hover:underline"
                    >
                      <ExternalLink className="h-3 w-3" />
                      {isRTL ? 'موقع الشريك' : 'Partner website'}
                    </a>
                  )}
                  {detail?.courseUrl && (
                    <a
                      href={detail.courseUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-foreground/75 hover:text-accent transition-colors"
                    >
                      <ExternalLink className="h-3 w-3" />
                      {isRTL ? 'عرض على Champions Hub' : 'View on Champions Hub'}
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* Sticky footer — two slot buttons + close. Enrollment is in ONE course. */}
            <div className="flex-shrink-0 border-t border-border/40 bg-surface-1/40">
              <div className="px-5 sm:px-6 pt-3 pb-2 text-[10px] font-mono-mark uppercase tracking-widest text-muted-foreground text-center">
                {t('initiative.coaches.form.preferredCourseEnrollmentNote')}
              </div>
              <DialogFooter className="px-5 sm:px-6 pb-3 flex-row gap-2 sm:justify-between flex-wrap">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onClose}
                  className="font-bold uppercase tracking-wider text-[11px]"
                >
                  {isRTL ? 'إغلاق' : 'Close'}
                </Button>

                <div className="flex gap-2 flex-wrap">
                  <Button
                    type="button"
                    size="sm"
                    variant={currentSlot === 'primary' ? 'default' : 'outline'}
                    onClick={() => {
                      onAssign(course.id, 'primary');
                      onClose();
                    }}
                    className={cn(
                      'gap-1.5 font-bold uppercase tracking-wider text-[11px]',
                      currentSlot === 'primary' && 'btn-glow'
                    )}
                  >
                    <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-accent text-accent-foreground font-display font-black text-[9px]">
                      1
                    </span>
                    {currentSlot === 'primary'
                      ? (isRTL ? 'إزالة من الأساسي' : 'Remove primary')
                      : primaryFilled
                        ? (isRTL ? 'استبدل بالأساسي' : 'Replace primary')
                        : (isRTL ? 'اختر كأساسي' : 'Set as primary')}
                  </Button>

                  <Button
                    type="button"
                    size="sm"
                    variant={currentSlot === 'alternate' ? 'default' : 'outline'}
                    onClick={() => {
                      onAssign(course.id, 'alternate');
                      onClose();
                    }}
                    className={cn(
                      'gap-1.5 font-bold uppercase tracking-wider text-[11px]',
                      currentSlot === 'alternate' && 'btn-glow'
                    )}
                  >
                    <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-accent/30 border border-accent text-accent font-display font-black text-[9px]">
                      2
                    </span>
                    {currentSlot === 'alternate'
                      ? (isRTL ? 'إزالة من البديل' : 'Remove alternate')
                      : alternateFilled
                        ? (isRTL ? 'استبدل بالبديل' : 'Replace alternate')
                        : (isRTL ? 'اختر كبديل' : 'Set as alternate')}
                  </Button>
                </div>
              </DialogFooter>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
